import { useCallback, useEffect, useMemo } from 'react'

import { differenceInMinutes } from 'date-fns/differenceInMinutes'
import { FormProvider, useForm } from 'react-hook-form'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import type { DialogProps } from '@/dialogs/types'
import {
  displayTime,
  getDateWithHoursAndMinutesFromTimeObject,
  getHoursAndMinutes,
  getOverlappingIntervalsIndexes,
  normalizeInterval,
  parseTime,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import { CalendarTimePicker } from '@/modules/online-mass-messages/ui/time-calendar/calendar-time-picker.tsx'

import { TimeWheel } from './TimeWheel'

const DEFAULT_FROM_DATE = new Date(new Date().setHours(0, 0, 0, 0))

DEFAULT_FROM_DATE.setHours(0, 0)
const DEFAULT_TO_DATE = new Date()

DEFAULT_TO_DATE.setHours(23, 59)

export interface CustomTimePopupFormValues {
  fromTime: Date
  toTime: Date
}
export interface BookedIntervals {
  start: string
  end: string
}

interface Props extends DialogProps {
  title: string
  fromTime?: Date | null
  toTime?: Date | null
  onClose: () => void
  id?: number
  update: (formValues: CustomTimePopupFormValues) => void
  bookedIntervals: BookedIntervals[]
}

export const SelectTimePopupDialog = ({
  isOpen,
  onClose,
  title,
  fromTime,
  toTime,
  update,
  bookedIntervals,
}: Props) => {
  const form = useForm<CustomTimePopupFormValues>({
    defaultValues: {
      fromTime: fromTime || DEFAULT_FROM_DATE,
      toTime: toTime || DEFAULT_TO_DATE,
    },
  })

  useEffect(() => {
    form.reset({
      fromTime: fromTime || DEFAULT_FROM_DATE,
      toTime: toTime || DEFAULT_TO_DATE,
    })
  }, [form, fromTime, toTime])

  const [fromTimeWatchValue, toTimeWatchValue] = form.watch([
    'fromTime',
    'toTime',
  ])

  const currentDuration = useMemo(() => {
    const selectedTimeFrame = normalizeInterval({
      start: getDateWithHoursAndMinutesFromTimeObject(
        getHoursAndMinutes(fromTimeWatchValue),
      ),
      end: getDateWithHoursAndMinutesFromTimeObject(
        getHoursAndMinutes(toTimeWatchValue),
      ),
    })

    return differenceInMinutes(selectedTimeFrame.end, selectedTimeFrame.start)
  }, [fromTimeWatchValue, toTimeWatchValue])

  const formattedDuration =
    currentDuration === 0
      ? `${currentDuration} minutes`
      : `${Math.floor(currentDuration / 60)} h ${currentDuration % 60} m`

  const overlappingIntervalsIndexes = getOverlappingIntervalsIndexes(
    fromTimeWatchValue,
    toTimeWatchValue,
    bookedIntervals,
  )
  const isTimeFrameInvalid = !!overlappingIntervalsIndexes.length

  const handleSave = useCallback(
    (values: CustomTimePopupFormValues) => {
      if (isTimeFrameInvalid) {
        return
      }
      update(values)
      onClose()
    },
    [onClose, isTimeFrameInvalid, update],
  )

  return (
    <Dialog
      show={isOpen}
      bigDialog={true}
      onClose={onClose}
      panelClassName="lg:max-w-xl w-[95vw] max-h-[90vh] overflow-y-auto"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description className="text-center">
              Select a time window during which these messages will be sent to
              fans automatically.
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Description>
            <div className="relative mt-4 sm:mt-6">
              <div>
                <div className="flex flex-col gap-4">
                  <div className="flex w-full items-center gap-2 sm:flex-1">
                    <span className="w-20 text-sm font-medium">Start time</span>
                    <CalendarTimePicker
                      name="fromTime"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex w-full items-center gap-2 sm:flex-1">
                    <span className="w-20 text-sm font-medium">End time</span>
                    <CalendarTimePicker
                      name="toTime"
                      className="flex-1"
                    />
                  </div>

                  <div className="flex w-full items-center gap-2 sm:flex-1">
                    <span className="w-20 text-sm font-medium">Duration:</span>
                    <span className="text-title text-sm font-medium">
                      {formattedDuration}
                    </span>
                  </div>
                </div>

                <div className="mt-2 h-6">
                  {isTimeFrameInvalid && (
                    <div className="text-sm text-red-600">
                      This time range overlaps with existing schedules
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!!bookedIntervals.length && (
              <>
                <Separator className="mb-4 sm:mb-6" />

                <div className="mt-2">
                  <div className="text-title mb-2 flex justify-center gap-1 font-semibold sm:text-lg">
                    Existing Timeframes{' '}
                    <Popover>
                      <PopoverTrigger>
                        <InfoIcon className="h-4 w-4 flex-shrink-0" />
                      </PopoverTrigger>
                      <PopoverContent className="z-[1000]">
                        <div className="grid max-h-[30vh] gap-2 overflow-y-auto p-1 sm:gap-3">
                          {bookedIntervals.map((interval, index) => {
                            return (
                              <div
                                key={interval.end}
                                className="relative rounded-lg border border-gray-200 p-2 text-sm transition-colors sm:p-3 sm:text-base"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {displayTime(parseTime(interval.start))}
                                    </span>
                                    <span className="text-gray-500">→</span>
                                    <span className="font-medium">
                                      {displayTime(parseTime(interval.end))}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="mt-1">
                    <TimeWheel
                      selectedStartTime={fromTimeWatchValue}
                      selectedEndTime={toTimeWatchValue}
                      bookedIntervals={bookedIntervals.map(
                        (interval, index) => ({
                          start: parseTime(interval.start),
                          end: parseTime(interval.end),
                          isConflicting:
                            overlappingIntervalsIndexes.includes(index),
                        }),
                      )}
                    />
                  </div>
                </div>
              </>
            )}
          </Dialog.Description>

          <Dialog.Footer>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex flex-1 items-center gap-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isTimeFrameInvalid || currentDuration <= 0}
              className="w-full text-sm sm:flex-1 sm:text-base"
            >
              Save
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
